import { Injectable } from '@angular/core';
import { MessageNotificationService } from 'message-notification-service-lib';
import { ReplaySubject } from 'rxjs';
import { IEntityMeta, IFieldMeta } from '../../../interfaces/imetadata';
import { ContentControlToFieldMapService, SerializedFieldContext } from '../../../services/content-control-to-field-map.service';
import { ActiveContentControlMonitor } from '../../../services/document-monitors/active-content-control-monitor';
import { ContentControlId } from '../../../services/document-monitors/content-control-id';
import { FieldConfig } from '../../../utils/fieldconfig';
import { FieldType } from '../../../utils/fieldtype';
import { DocumentSenderService } from './document-sender.service';

export class FieldAndConfig {
  field: IFieldMeta;
  config: FieldConfig;

  constructor(field: IFieldMeta, config: FieldConfig) {
    this.field = field;
    this.config = config;
  }
}

export class ValidDocumentSenderFields {
  fields: IFieldMeta[] | FieldAndConfig;

  constructor(fields: IFieldMeta[] | FieldAndConfig) {
    this.fields = fields;
  }
}

@Injectable({ providedIn: 'root' })
export class DocumentSenderFieldProviderService {
  private validFieldsSubject = new ReplaySubject<ValidDocumentSenderFields | undefined>(1);
  validFieldsChanged$ = this.validFieldsSubject.asObservable();
  private documentSenderEntity: IEntityMeta | undefined;

  constructor(
    private contentControlToFieldMapService: ContentControlToFieldMapService,
    private documentSenderService: DocumentSenderService,
    private messageNotificationService: MessageNotificationService,
    activeContentControlMonitor: ActiveContentControlMonitor
  ) {
    activeContentControlMonitor.activeContentControlChanged$.subscribe((acc) => this.activeControlChanged(acc));
  }

  initialise(namespace: string, token: string): void {
    this.documentSenderService.getFields(namespace, token).subscribe({
      next: (entity) => {
        this.documentSenderEntity = entity;
        this.promoteCompositeFields();
      },
      error: (error) => this.messageNotificationService.error('Failed to load document sender fields.', error),
    });
  }

  lookup(path: string): IFieldMeta | undefined {
    return this.documentSenderEntity?.fields.find((f) => f.path === path);
  }

  private promoteCompositeFields(): void {
    const entityFields = this.documentSenderEntity!.fields;

    // do this if we want to keep the composite fields but make them look like first class fields (i.e. belong to entity)
    /*
    const len = entityFields.length;
    for (let i = 0; i<len; i++) {
      const f = entityFields[i];
      if (f.type === FieldType.COMPOSITE) {
        f.childrenOfComposite?.forEach(cf => {
          const nf = {...cf};
          nf.label = nf.label !== nf.path ? nf.label : nf.path.split('.').pop()!;
          entityFields.push(nf)
        });
      }
    }
    */

    // now remove the composite
    this.documentSenderEntity!.fields = entityFields.filter((f) => f.type !== FieldType.COMPOSITE);
  }

  private isConditionalField(activeContentControl: ContentControlId): boolean {
    return activeContentControl.tag.startsWith('od:condition');
  }

  private activeControlChanged(activeContentControl: ContentControlId | undefined): void {
    console.debug('%cdocument-sender-field - active content control changed handler', 'color:#aa00aa');

    if (!this.documentSenderEntity) {
      this.validFieldsSubject.next(undefined);
      return;
    }

    if (!activeContentControl || this.isConditionalField(activeContentControl)) {
      const validFields = new ValidDocumentSenderFields(this.documentSenderEntity.fields);
      this.validFieldsSubject.next(validFields);
      return;
    }

    // TODO: think about this - what happens when we're it's a non-field content control inside a field content control?
    const activeFieldContext = this.contentControlToFieldMapService.get(activeContentControl.id);
    if (!activeFieldContext) {
      // we might be in a content control that doesn't belong to us?
      this.validFieldsSubject.next(undefined); // TODO: think about this one - what if inside one of ours?
      return;
    }

    // could be a 'normal' field for example
    if (!this.isDocumentSenderField(activeFieldContext.entity)) {
      this.validFieldsSubject.next(undefined);
      return;
    }

    const activeField = this.documentSenderEntity.fields.find((f) => f.path === activeFieldContext.path);
    if (!activeField) {
      throw new Error(`Active field '${activeFieldContext.path}' not found.`);
    }

    const activeConfig = this.serializedFieldContextToFieldConfigAdapter(activeFieldContext);

    // standard across all rule engines?
    const isLeafField = !activeField.fields;
    if (isLeafField) {
      this.validFieldsSubject.next(new ValidDocumentSenderFields(new FieldAndConfig(activeField, activeConfig)));
      return;
    }

    // should it ever get here?
    console.debug(`Unexpected scenario for '${activeFieldContext.path}'.`);
    this.validFieldsSubject.next(undefined);
  }

  private serializedFieldContextToFieldConfigAdapter(controlData: SerializedFieldContext): FieldConfig {
    return {
      dateFormatOptions: controlData.dateFormatOptions,
      integerFormat: controlData.integerFormat,
      decimalFormat: controlData.decimalFormat,
      conditions: controlData.conditions,
      sortOption: controlData.sortOption,
      limit: controlData.limit,
    };
  }

  private isDocumentSenderField(entity: string): boolean {
    return entity === this.documentSenderEntity?.entity;
  }
}
